import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert } from 'react-bootstrap';
import { Globe, Settings } from 'lucide-react';
import { WebSourceFormData } from '../../../types';

interface WebSourceFormProps {
    initialData?: WebSourceFormData;
    isEdit?: boolean;
    onSubmit: (data: WebSourceFormData) => Promise<void>;
    onCancel: () => void;
    onDelete?: () => Promise<void>;
    error?: string | null;
    isLoading?: boolean;
}

const renderSectionHeader = (icon: React.ReactNode, title: React.JSX.Element) => (
    <div className="d-flex align-items-center mb-2">
        <div className="rounded-circle p-2 bg-secondary bg-opacity-10 me-2">
            {icon}
        </div>
        <h6 className="mb-0 fw-medium">{title}</h6>
    </div>
);

const WebSourceForm: React.FC<WebSourceFormProps> = ({
                                                         initialData,
                                                         isEdit = false,
                                                         onSubmit,
                                                         onCancel,
                                                         onDelete,
                                                         error,
                                                         isLoading
                                                     }) => {
    const [formData, setFormData] = useState<WebSourceFormData>({
        name: '',
        description: '',
        url: '',
        crawl_frequency: 'daily',
        sitemap_url: '',
        crawl_depth: 2
    });

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name || '',
                description: initialData.description || '',
                url: initialData.url || '',
                crawl_frequency: initialData.crawl_frequency || 'daily',
                sitemap_url: initialData.sitemap_url || '',
                crawl_depth: initialData.crawl_depth || 2
            });
        }
    }, [initialData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSubmit(formData);
    };

    const handleChange = (field: keyof WebSourceFormData, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
                {error && (
                    <Alert variant="danger" className="mb-3 py-2 px-3">
                        {error}
                    </Alert>
                )}

                <Form onSubmit={handleSubmit} className="mx-auto" style={{ maxWidth: '1100px' }}>
                    {/* Basic Information */}
                    <div className="mb-3">
                        {renderSectionHeader(
                            <Globe size={16} className="text-secondary" />,
                            <>Web Source Information <span className="text-danger">*</span></>
                        )}
                        <Form.Group className="mb-2">
                            <Form.Label className="fw-medium small mb-1">
                                <span className="text-danger me-1">*</span>
                                Name
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                required
                                className="border"
                                style={{ height: '38px' }}
                            />
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label className="fw-medium small mb-1">
                                <span className="text-danger me-1">*</span>
                                Website URL
                            </Form.Label>
                            <Form.Control
                                type="url"
                                value={formData.url}
                                onChange={(e) => handleChange('url', e.target.value)}
                                required
                                className="border"
                                style={{ height: '38px' }}
                                placeholder="https://example.com"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="fw-medium small mb-1">Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                className="border"
                            />
                        </Form.Group>
                    </div>

                    {/* Crawling Settings */}
                    <div className="mb-3">
                        {renderSectionHeader(
                            <Settings size={16} className="text-secondary" />,
                            <>Crawling Settings <span className="text-danger">*</span></>
                        )}
                        <div className="row g-2">
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-medium small mb-1">
                                        <span className="text-danger me-1">*</span>
                                        Crawl Frequency
                                    </Form.Label>
                                    <Form.Select
                                        value={formData.crawl_frequency}
                                        onChange={(e) => handleChange('crawl_frequency', e.target.value)}
                                        required
                                        className="form-select border"
                                        style={{ height: '38px', paddingTop: '0.375rem', paddingBottom: '0.375rem' }}
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </Form.Select>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="fw-medium small mb-1">
                                        <span className="text-danger me-1">*</span>
                                        Crawl Depth
                                        <span className="ms-1 badge bg-secondary bg-opacity-10 text-secondary">
                                            Default: 2
                                        </span>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={formData.crawl_depth}
                                        onChange={(e) => handleChange('crawl_depth', parseInt(e.target.value, 10))}
                                        min={1}
                                        max={5}
                                        required
                                        className="border"
                                        style={{ height: '38px' }}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-12">
                                <Form.Group>
                                    <Form.Label className="fw-medium small mb-1">
                                        Sitemap URL (Optional)
                                    </Form.Label>
                                    <Form.Control
                                        type="url"
                                        value={formData.sitemap_url}
                                        onChange={(e) => handleChange('sitemap_url', e.target.value)}
                                        className="border"
                                        style={{ height: '38px' }}
                                        placeholder="https://example.com/sitemap.xml"
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-3">
                        {isEdit && onDelete ? (
                            <Button
                                variant="danger"
                                onClick={onDelete}
                                disabled={isLoading}
                                style={{ height: '38px' }}
                            >
                                Delete
                            </Button>
                        ) : <div />}

                        <div className="d-flex gap-2">
                            <Button
                                variant="light"
                                onClick={onCancel}
                                disabled={isLoading}
                                style={{ height: '38px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isLoading}
                                style={{ height: '38px' }}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" />
                                        {isEdit ? 'Saving...' : 'Creating...'}
                                    </>
                                ) : (
                                    isEdit ? 'Save' : 'Create Web Source'
                                )}
                            </Button>
                        </div>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default WebSourceForm;